import { ContactFormSection } from "@/types/dto/destinations-page/destination-page-types";
import CheckIcon from "@mui/icons-material/Check";
import { Box, Typography, Grid } from "@mui/material";
import React from "react";
import { ColletteButton } from "@collette-travel/collette-common-controls";
import ContactForm from "@/components/forms/contact-form-section/contact-form";
import { SectionHeader } from "../../common/section-header";

interface ContactFormSectionProps {
  contactFormSection: ContactFormSection;
  moduleRankingPosition?: string;
}

const ContactFormSectionComponent: React.FC<ContactFormSectionProps> = ({
  contactFormSection,
  moduleRankingPosition = "17"
}) => (
  <Box
    className="fluid-box anchor-section"
    sx={{
      backgroundColor: "background.warm_strong",
      display: "flex",
      alignItems: "center",
      gap: "40px",
      alignSelf: "stretch"
    }}
    data-track="visibility"
    data-parent-container-type="form"
    data-module-name="Contact Form Section"
    data-ranking-algorithm="manual"
    data-module-ranking-position={moduleRankingPosition}
    component="section"
  >
    <Grid
      container
      spacing={5}
      className="destination-page-container"
      sx={{
        padding: {
          xs: "4rem 2.4rem",
          sm: "4rem 3.2rem",
          md: "6rem clamp(2.4rem, 4.5vw, 6.5rem)"
        }
      }}
    >
      <Grid size={{ xs: 12, md: 12, lg: 5 }}>
        <Box height="100%" sx={{ gap: "2.4rem" }}>
          <SectionHeader
            headerText={contactFormSection.contactFormIntro.headerText}
            copyText={contactFormSection.contactFormIntro.copyText}
            headerTestId="contact-form-header"
            copyTestId="contact-form-copy"
            sx={{ color: "text.primary", paddingBottom: "2.4rem" }}
            headerSx={{
              textTransform: "none !important",
              fontSize: { xs: "2.6rem", sm: "2.8rem", lg: "3.2rem" },
              textWrap: "wrap"
            }}
          />
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "1.6rem",
              paddingBottom: "2.4rem"
            }}
          >
            {contactFormSection.bulletItems.map((item, key) => (
              <Box key={item} sx={{ display: "flex", alignItems: "center", gap: "0.8rem" }}>
                <CheckIcon sx={{ color: "text.primary", fontWeight: "500" }} />
                <Typography
                  variant="subtitle1"
                  component="p"
                  sx={{ color: "text.primary", fontWeight: "500", lineHeight: "3rem" }}
                  data-testid={`bullet-item-${key}`}
                >
                  {item}
                </Typography>
              </Box>
            ))}
            <Box>
              <Typography
                variant="h6"
                component="p"
                sx={{
                  color: "text.primary",
                  fontSize: "1.8rem",
                  fontWeight: "600 !important",
                  paddingBottom: "1.2rem",
                  height: "58px",
                  display: "flex",
                  alignItems: "start",
                  gap: "0.8rem",
                  flexDirection: { xs: "column", sm: "row" }
                }}
                data-testid="contact-form-call-to-action"
              >
                {contactFormSection.contactFormIntro.callToActionText}
                <a
                  href={`tel:${contactFormSection.phoneNumber}`}
                  style={{
                    fontWeight: "600",
                    fontSize: "1.8rem",
                    color: "var(--theme-palette-secondary-main)",
                    textDecoration: "underline"
                  }}
                  data-testid="contact-form-phone-number"
                  data-track="click"
                >
                  {contactFormSection.phoneNumber}
                </a>
              </Typography>
            </Box>
          </Box>
          <Box>
            <Typography
              variant="h5"
              component="h3"
              sx={{ paddingBottom: "1.2rem", color: "text.primary" }}
              data-testid="contact-form-login-cta-header"
            >
              {contactFormSection.contactFormLoginCTA.headerText}
            </Typography>
            <Typography
              variant="subtitle1"
              component="p"
              sx={{ paddingBottom: "2.4rem", color: "text.primary" }}
              data-testid="contact-form-login-cta-copy"
            >
              {contactFormSection.contactFormLoginCTA.copyText}
            </Typography>
            <ColletteButton
              variant="outlined"
              size="large"
              sx={{
                backgroundColor: "background.default"
              }}
              label="Log In"
              aria-describedby="login-popover"
              href="/profile/login"
              testId="contactform-login-button"
              data-track="click"
            />
          </Box>
        </Box>
      </Grid>
      <Grid size={{ xs: 12, md: 12, lg: 7 }}>
        <Box
          id="contact-us-form"
          height="100%"
          sx={{
            display: "flex",
            borderRadius: "1.25rem",
            backgroundColor: "background.default",
            padding: "2.5rem",
            boxShadow: 6,
            justifyContent: "center",
            alignItems: "center"
          }}
        >
          <ContactForm contactFormSection={contactFormSection} />
        </Box>
      </Grid>
    </Grid>
  </Box>
);
export default ContactFormSectionComponent;
